import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, RotateCcw, Save } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const periodLabel = (p) => { const [y, m] = p.split("-"); return `${MONTHS[parseInt(m, 10) - 1]} ${y}`; };

export default function Billing() {
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState("");
  const [payee, setPayee] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/billing/months");
      setRows(r.data.months);
      setAmount(String(r.data.amount));
      setPayee(r.data.payee);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveAmount = async () => {
    const a = parseFloat(amount);
    if (isNaN(a) || a <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try { await api.put("/billing/config", { amount: a }); toast.success("Monthly amount updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post("/billing/mark-paid", { period: payRow.period, note: note.trim() });
      toast.success(`${periodLabel(payRow.period)} marked paid`);
      setPayRow(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const unmark = async (p) => { await api.post("/billing/unmark", { period: p }); toast.success("Reverted to due"); load(); };

  const dueCount = rows.filter((r) => r.status === "due").length;

  return (
    <div className="space-y-6 fade-up" data-testid="billing-page">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2"><CalendarClock className="h-6 w-6 text-primary" /> Subscription billing</h1>
        <p className="text-sm text-muted-foreground">AUM Online monthly subscription. Mark a month paid after you receive Jay's payment screenshot.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-sm text-muted-foreground">Monthly amount</div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-lg">₹</span>
            <Input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} className="font-mono max-w-[120px]" data-testid="billing-amount-input" />
            <Button size="sm" onClick={saveAmount} disabled={busy} className="gap-1" data-testid="billing-amount-save"><Save className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Editable — e.g. if Emergent's 50-credit cost changes.</p>
        </Card>
        <Card className="p-5">
          <div className="text-sm text-muted-foreground">Payee</div>
          <div className="text-xl font-semibold mt-2">{payee || "—"}</div>
          <div className="text-xs text-muted-foreground">Vyapar.177783660386@hdfcbank</div>
        </Card>
        <Card className="p-5">
          <div className="text-sm text-muted-foreground">Months due</div>
          <div className={`kpi-number text-3xl font-semibold mt-2 ${dueCount > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="billing-due-count">{dueCount}</div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table data-testid="billing-table">
          <TableHeader>
            <TableRow><TableHead>Month</TableHead><TableHead>Due date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No billing months yet.</TableCell></TableRow>}
            {!loading && rows.map((m) => (
              <TableRow key={m.period} data-testid={`billing-row-${m.period}`}>
                <TableCell className="font-medium">{periodLabel(m.period)}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(m.due_date)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(m.amount)}</TableCell>
                <TableCell>
                  {m.status === "paid"
                    ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" /> Paid</Badge>
                    : <Badge variant="outline" className="border-amber-300 text-amber-700">Due</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {m.status === "due"
                    ? <Button size="sm" onClick={() => { setPayRow(m); setNote(""); }} data-testid={`billing-markpaid-${m.period}`}>Mark paid</Button>
                    : <Button size="sm" variant="outline" className="gap-1" onClick={() => unmark(m.period)} data-testid={`billing-unmark-${m.period}`}><RotateCcw className="h-3.5 w-3.5" /> Undo</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!payRow} onOpenChange={(o) => !o && setPayRow(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Mark {payRow && periodLabel(payRow.period)} as paid</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Confirm you received Jay's payment of {payRow && inr(payRow.amount)}. Add a note (optional, e.g. UPI ref).</p>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note / UPI reference (optional)" className="mt-2" data-testid="billing-paid-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayRow(null)}>Cancel</Button>
            <Button onClick={markPaid} disabled={busy} data-testid="billing-paid-confirm">Confirm paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
